import { useRef, useState } from 'react';
import styled from '@emotion/styled';
import HTMLWrapper from '@comp/Question/HTMLWrapper';
import WithPreviewImage from '@comp/Question/WithPreviewImage/WithPreviewImage';
import useQuestion from '@cont/Quiz/Taking/Objective/Question/useQuestion';
import Dangerous from '@educator/Container/AutoDPP/DangerousContent';
import { NTA_COLORS, SCROLL_NAV_RIGHT_OFFSET } from '../constants';
import {
  ntaPageCss,
  ntaQuestionContentCss,
  ntaQuestionTitleCss
} from '../typography';
import NtaObjectiveQuestion from './NtaObjectiveQuestion';
import NtaQuestionActions from './NtaQuestionActions';
import NtaQuestionScroll from './NtaQuestionScroll';
import NtaQuestionTitleRow from './NtaQuestionTitleRow';
import NtaScrollNavButton from './NtaScrollNavButton';

const Panel = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 16px 0 0 24px;
  ${ntaPageCss}
`;

const Columns = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  gap: 0;
  overflow: hidden;
`;

const Column = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &:first-of-type {
    border-right: 1px solid ${NTA_COLORS.questionBorder};
    padding-right: 16px;
  }

  &:last-of-type {
    padding-left: 16px;
  }
`;

const ColumnHeader = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px ${SCROLL_NAV_RIGHT_OFFSET}px 8px 0;
  border-bottom: 1px solid ${NTA_COLORS.questionBorder};
`;

const ColumnTitle = styled.h2`
  margin: 0;
  flex: 1;
  min-width: 0;
  ${ntaQuestionTitleCss}
`;

const PassageContent = styled.div`
  ${ntaQuestionContentCss}

  table {
    border-collapse: collapse;
    width: 100%;
  }

  td,
  th {
    border: 1px solid ${NTA_COLORS.questionBorder};
    padding: 4px 8px;
  }
`;

const NtaComprehensionView = () => {
  const { currentQuestion, currentLanguage, questionUID } = useQuestion();
  const passageScrollRef = useRef(null);
  const questionScrollRef = useRef(null);
  const [passageScrollMetrics, setPassageScrollMetrics] = useState({
    canScroll: false,
    atBottom: true
  });
  const [questionScrollMetrics, setQuestionScrollMetrics] = useState({
    canScroll: false,
    atBottom: true
  });

  const passageHtml = currentQuestion?.comprehension?.[currentLanguage?.code];

  if (!currentQuestion?.uid || !passageHtml) {
    return null;
  }

  return (
    <Panel>
      <Columns>
        <Column>
          <ColumnHeader>
            <ColumnTitle>Comprehension Passage</ColumnTitle>
            {passageScrollMetrics.canScroll &&
            !passageScrollMetrics.atBottom ? (
              <NtaScrollNavButton
                direction="down"
                onClick={() => passageScrollRef.current?.scrollToBottom()}
              />
            ) : null}
          </ColumnHeader>
          <NtaQuestionScroll
            ref={passageScrollRef}
            scrollKey={`${questionUID}-passage`}
            showScrollNav
            onScrollMetricsChange={setPassageScrollMetrics}
          >
            <WithPreviewImage>
              <PassageContent>
                <Dangerous
                  as={HTMLWrapper}
                  data-image-preview
                  $isImageClickable={false}
                  html={passageHtml}
                />
              </PassageContent>
            </WithPreviewImage>
          </NtaQuestionScroll>
        </Column>
        <Column>
          <NtaQuestionTitleRow
            onScrollToBottom={() => questionScrollRef.current?.scrollToBottom()}
            showScrollToBottom={
              questionScrollMetrics.canScroll && !questionScrollMetrics.atBottom
            }
          >
            Question {currentQuestion.question_rank}:
          </NtaQuestionTitleRow>
          <NtaQuestionScroll
            ref={questionScrollRef}
            scrollKey={`${questionUID}-question`}
            showScrollNav
            onScrollMetricsChange={setQuestionScrollMetrics}
          >
            <WithPreviewImage>
              <NtaObjectiveQuestion />
            </WithPreviewImage>
          </NtaQuestionScroll>
        </Column>
      </Columns>
      <NtaQuestionActions />
    </Panel>
  );
};

export default NtaComprehensionView;
